import { getAppGuid } from "@/components/elements/search/utility";
import { currentPageIsAContentPage, getContentIdFromUrl, getContentTitleFromContentTitleInput, getParentIdFromUrl, hasParentContentUrlHref, homeContentUrlHref, menuContentUrlHref, noParentContentUrlHref } from "./Content";
import { BREADCRUMB_MENU_SESSION_KEY, BREADCRUMB_SESSION_KEY, IBreadCrumb } from  "@/interfaces/IBreadCrumbs";



export function buildBreadcrumbs(url: string, storedBreadCrumbData: IBreadCrumb[]): IBreadCrumb[] {
    const pageTitle = getContentTitleFromContentTitleInput();
    const isContentPage = currentPageIsAContentPage(url);
    const contentId = getContentIdFromUrl(url);
    const parentId = getParentIdFromUrl(url);
    const menuId = getMenuId();
    const parsedUrl = new URL(url);
    const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
    const queryParams = Object.fromEntries(parsedUrl.searchParams.entries());
    const isMenuPage = !isContentPage && pathParts.length === 2;
    const homeLink = queryParams.homeLink === "True";
    
    // only create breadcrumbs if user is on Menu page / Home page / Content page
    if (!(isMenuPage || isContentPage)) {
        return [];
    }

    let breadcrumbs = [...storedBreadCrumbData];
    const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    const contentMapsToParent = previousBreadcrumb?.id && previousBreadcrumb.id === parentId
    const contentBreadCrumbsAreDifferentFromUrl = isContentPage && contentId && parentId && previousBreadcrumb?.id && !contentMapsToParent
    const contentExistsAsABreadCrumbAlready = isContentPage && contentId && breadcrumbs.some((b) => b.id === contentId);
    const breadcrumbsLastItemHasNoIdYet = isContentPage && !contentId && !breadcrumbs.at(-1)?.id;
    const contentJustPosted = isContentPage && contentId && !breadcrumbs.at(breadcrumbs.length -1)?.id && breadcrumbs.at(breadcrumbs.length -2)?.id === parentId;
    const directlyLinkedToContentItem = breadcrumbs.length && !breadcrumbs.some((breadcrumb) => breadcrumb.label === "Menu" || breadcrumb.label === "Home");
    const isHomePage = isContentPage && homeLink;
    const isDirectlyLinkedToContentOrBase = (isMenuPage || isContentPage) && directlyLinkedToContentItem;
    const parentIdMatches = !isHomePage && !isMenuPage && (previousBreadcrumb?.id === parentId);
    const isNewBreadCrumbPage = !contentExistsAsABreadCrumbAlready && contentId;
    
    if (isDirectlyLinkedToContentOrBase) {
        // then we clear the breadcrumbs before we build the breadcrumbs back up
        breadcrumbs = [];
    }

    if (isMenuPage) {
        return [{ label: "Menu", id: menuId }];
    }

    if (isHomePage) {
        return [{ label: "Home", id: contentId }];
    }

    if (contentExistsAsABreadCrumbAlready) {
        // then we keep only breadcrumbs up to that content item within the breadcrumbs already
        const indexOfBreadCrumb = breadcrumbs.findIndex((b) => b.id === contentId);
        breadcrumbs = breadcrumbs.slice(0, indexOfBreadCrumb + 1);

        return breadcrumbs;
    }

    if (breadcrumbsLastItemHasNoIdYet) {
        //if we are creating new content from menu, we create a new breadcrumb
        const lastCrumb = breadcrumbs.at(-1);
        if (lastCrumb?.label === "Menu" && !lastCrumb.id && pageTitle) {
            breadcrumbs = [
                ...breadcrumbs,
                { label: pageTitle, id: contentId, parentId }
            ]
        }

        return breadcrumbs
    }

    if (contentJustPosted) {
        breadcrumbs = [
            ...breadcrumbs.slice(0, -1),
            { label: pageTitle, id: contentId, parentId }
        ]

        return breadcrumbs
    }

    // If parentId does not match the previous breadcrumb id, 
    // we can assume they have navigated directly to content
    // Menu > ... > current page title
    if (contentBreadCrumbsAreDifferentFromUrl) {
        breadcrumbs = [
            { label: "Menu", id: menuId },
            { label: "...", id: parentId },
            { label: pageTitle, id: contentId, parentId }
        ];

        return breadcrumbs;
    }

    if ((isNewBreadCrumbPage) || (!contentId)) {     
        const isDeeplinkedWithParentId = breadcrumbs.length === 0 && parentId;
        const isDeeplinkedWithoutParentId = breadcrumbs.length === 0 && !parentId;
        const hasParentWhichDoesNotMatchExistingBreadCrumbs = parentId && !parentIdMatches;   

        if (isDeeplinkedWithParentId || hasParentWhichDoesNotMatchExistingBreadCrumbs) {
            breadcrumbs = [
                { label: "Menu", id: menuId },
                { label: "...", id: parentId },
                { label: pageTitle, id: contentId, parentId }
            ];

        } else if (isDeeplinkedWithoutParentId){
            breadcrumbs = [
                { label: "Menu", id: menuId },
                { label: pageTitle, id: contentId, parentId }
            ];
        } else {
            breadcrumbs = [
                ...breadcrumbs,
                { label: pageTitle, id: contentId, parentId }
            ];
        }

        return breadcrumbs
    }
    
    return breadcrumbs
}


export function getInitialBreadCrumbs() {
    const saved = sessionStorage.getItem(BREADCRUMB_SESSION_KEY);
    return saved ? JSON.parse(saved) : [];
}

export function getMenuId() {
    const id = sessionStorage.getItem(BREADCRUMB_MENU_SESSION_KEY);
    return id ?? null;
}

export function syncTitleFromInput(setBreadCrumbs: any) {
    //check if there is a title input on the screen
    const titleInput = document.querySelector<HTMLInputElement>("input#Title.editableText");
    if (!titleInput) return () => {};

    const syncTitle = () => {
        setBreadCrumbs((allBreadcrumbs: IBreadCrumb[]) => {
            //if it is the Menu or the Home Page we already hardcode the label.
            if (allBreadcrumbs.length === 1) return allBreadcrumbs;

            //if the last breadcrumb is a new piece of content, we need to update the input
            const lastIndex = allBreadcrumbs.length - 1;
            const lastBreadCrumb = allBreadcrumbs[lastIndex];
            const newLabel = titleInput.value.trim() || titleInput.placeholder.trim();

            if (!lastBreadCrumb.id || (lastBreadCrumb?.id && newLabel && lastBreadCrumb.label !== newLabel)) {
                const newLabel = titleInput.value.trim() || titleInput.placeholder.trim();
                const updatedBreadCrumbs = [...allBreadcrumbs];
                updatedBreadCrumbs[lastIndex] = { ...lastBreadCrumb, label: newLabel };
                sessionStorage.setItem(BREADCRUMB_SESSION_KEY, JSON.stringify(updatedBreadCrumbs));

                return updatedBreadCrumbs;
            }

            return allBreadcrumbs;
        });
    };

    //whenever the user is typing the title or changes a title then the breadcrumb should update
    titleInput.addEventListener("input", syncTitle);
    titleInput.addEventListener("change", syncTitle);

    syncTitle();

    //cleanup
    return () => {
        titleInput.removeEventListener("input", syncTitle);
        titleInput.removeEventListener("change", syncTitle);
    };
}


//listen to the browser history navigation and dom changes detect the url changes
//and then call the function to update the breadcrumbs
export function observeUrlChanges(onChange: any) {
    const handlePopState = () => onChange();
    window.addEventListener("popstate", handlePopState);

    let previousUrl = window.location.href;
    const observer = new MutationObserver(() => {
        if (window.location.href !== previousUrl) {
            previousUrl = window.location.href;
            onChange();
        }
    });

    observer.observe(document, { subtree: true, childList: true, attributes: true });

    return () => {
        window.removeEventListener("popstate", handlePopState);
        observer.disconnect();
    };
}


export function patchBreadcrumbWhenContentIdAppears(breadCrumbs: IBreadCrumb[], setBreadCrumbs: any) {
    const interval = setInterval(() => {
        const contentId = getContentIdFromUrl(window.location.href);
        const parentId = getParentIdFromUrl(window.location.href);
        const isContentPage = currentPageIsAContentPage(window.location.href);
        const lastCrumb = breadCrumbs.at(-1);

        const shouldPatchCrumb = isContentPage && contentId && lastCrumb && !lastCrumb.id;

        if (shouldPatchCrumb) {
            const updated = [...breadCrumbs];
            updated[updated.length - 1] = {
                ...lastCrumb,
                id: contentId,
                parentId,
                label: document.title || lastCrumb.label,
            };

            setBreadCrumbs(updated);
            sessionStorage.setItem(BREADCRUMB_SESSION_KEY, JSON.stringify(updated));

            clearInterval(interval);
        }
    }, 500);

    return () => clearInterval(interval);
}


export function buildBreadCrumbUrl(crumb: IBreadCrumb) {
    const appGuid = getAppGuid();

    if (appGuid) {
        if (crumb.label === "Menu") {
            return menuContentUrlHref(appGuid);
        }

        if (crumb.id) {
            if (crumb.label === "Home") {
                return homeContentUrlHref(appGuid, crumb.id);
            }
            if (crumb.parentId && getAppGuid()) {
                return hasParentContentUrlHref(appGuid, crumb.id, crumb.parentId);
            } else {
                return noParentContentUrlHref(appGuid, crumb.id);
            }
        }
    }
    return "../../";
}